import { createRoot } from "react-dom/client";
import "@fontsource/schibsted-grotesk/400.css";
import "@fontsource/schibsted-grotesk/500.css";
import "@fontsource/schibsted-grotesk/600.css";
import "@fontsource/schibsted-grotesk/700.css";
import "./index.css";
import { initErrorTracking } from "./lib/errorTracking";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { validateEnv } from "./config/env";
import { showFatalErrorInRoot } from "./lib/bootstrapError";
import { logger } from "@/lib/logger";

let bootstrapOk = true;

try {
  validateEnv();
} catch (err: unknown) {
  bootstrapOk = false;
  const message = err instanceof Error ? err.message : "Unknown configuration error";
  showFatalErrorInRoot("Configuration error", message);
  logger.error("Configuration error", err);
}

if (bootstrapOk) {
  try {
    initErrorTracking();
  } catch (err: unknown) {
    bootstrapOk = false;
    const message = err instanceof Error ? err.message : "Unknown error during error tracking init";
    showFatalErrorInRoot("Startup error", message);
    logger.error("Startup error", err);
  }
}

function mountPostHog(): void {
  if (typeof window === "undefined") return;
  if ("requestIdleCallback" in window) {
    requestIdleCallback(() => {
      import("./lib/posthog").then(({ initPostHog }) => {
        initPostHog();
      });
    });
  } else {
    setTimeout(() => {
      import("./lib/posthog").then(({ initPostHog }) => {
        initPostHog();
      });
    }, 1000);
  }
}

if (bootstrapOk) {
  void import("./App.tsx")
    .then(({ default: App }) => {
      const rootEl = document.getElementById("root");
      if (!rootEl) return;
      createRoot(rootEl).render(
        <ErrorBoundary
          fallbackTitle="Application Error"
          fallbackDescription="The application failed to start. Please try refreshing the page."
          showHomeButton={false}
        >
          <App />
        </ErrorBoundary>
      );
      mountPostHog();
    })
    .catch((err: unknown) => {
      const message = err instanceof Error ? err.message : String(err);
      showFatalErrorInRoot("Failed to load app", message);
      logger.error("Failed to load app", err);
    });
}
